import { _ as _ts_decorate } from "@swc/helpers/_/_ts_decorate";
import { _ as _ts_metadata } from "@swc/helpers/_/_ts_metadata";
import { action, computed, flow, observable, runInAction } from "mobx";
import { routeLocationAffiliatePost, routeLocationAffiliateProductsGet, routeLocationEnvatoPurchasePost, routeLocationPaymentFailedPaymentDetailsGet, routeLocationUserDoubleOptInPatch, routeLocationUserEmailActivatePatch, routeLocationUserSessionMagicLinkPost, routeLocationUserSessionPost } from "@devowl-wp/api-real-commerce";
import { BaseOptions } from "@devowl-wp/utils";
import { RealCommerceError } from "../../../exceptions/realCommerceError.js";
import { trackAnalyticsEvent } from "../../../others/tracking.js";
import { NotificationChannel } from "../../../store/notification.js";
import { LOCAL_STORAGE_JWT_TOKEN_NAME } from "../../../utils/jwtTokenStorage.js";
import { localStorageTest } from "../../../utils/localStorageTest.js";
import { realCommerceRequest } from "../../../utils/realCommerceRequest.js";
import { PaddleSubscription } from "../models/paddleSubscription.js";
import { UserSession } from "../models/userSession.js";
class AccountV2Store extends BaseOptions {
    get isLoggedIn() {
        return !!this.session?.isLoggedIn;
    }
    get isAffiliate() {
        return !!this.session?.affiliate;
    }
    constructor(rootStore){
        super(), this.isBusyAffiliateProducts = false, this.affiliateProducts = [], this.expiredPaymentAffectedHostnames = [], this.createSessionFromJwt = flow(function*(jwt) {
            const session = new UserSession(jwt, this);
            yield session.probablyRenewJwt();
            yield session.fetchUser();
            this.session = session;
        }), this.sendMagicLink = flow(function*(email) {
            try {
                yield realCommerceRequest({
                    location: routeLocationUserSessionMagicLinkPost,
                    request: {
                        user: {
                            email
                        }
                    }
                });
                this.submitted = {
                    email
                };
            } catch (e) {
                throw new RealCommerceError(e);
            }
        }), this.receiveJwt = flow(function*(magicLinkToken) {
            try {
                const response = yield realCommerceRequest({
                    location: routeLocationUserSessionPost,
                    request: {
                        userSession: {
                            magicLinkToken
                        }
                    }
                });
                return response.jwt;
            } catch (e) {
                throw new RealCommerceError(e);
            }
        }), this.confirmDoubleOptIn = flow(function*(confirmationCode) {
            try {
                return yield realCommerceRequest({
                    location: routeLocationUserDoubleOptInPatch,
                    request: {
                        user: {
                            confirmationCode
                        }
                    }
                });
            } catch (e) {
                throw new RealCommerceError(e);
            }
        }), this.updateEmailAddress = flow(function*(newEmailConfirmationCode) {
            try {
                yield realCommerceRequest({
                    location: routeLocationUserEmailActivatePatch,
                    request: {
                        user: {
                            newEmailConfirmationCode
                        }
                    }
                });
                yield this.session?.fetchUser();
            } catch (e) {
                throw new RealCommerceError(e);
            }
        }), this.fetchSubscriptionsAndLicenses = flow(function*() {
            if (!this.session) {
                return;
            }
            // Renew the JWT once up front so the parallel child fetches below find a
            // fresh token and their internal `probablyRenewJwt()` calls no-op out.
            yield this.session.probablyRenewJwt();
            yield Promise.all([
                this.session.fetchSubscriptions(),
                this.session.fetchLicenses()
            ]);
        }), this.addLicense = flow(function*(subscription, input) {
            return yield subscription.addLicense(input);
        }), this.editLicense = flow(function*(license, input) {
            yield license.updateDetails(input);
        }), this.deleteLicense = flow(function*(license) {
            yield license.delete();
        }), this.revokeActivation = flow(function*(license, activation) {
            yield license.revokeActivation(activation);
        }), this.applyChangePlan = flow(function*(subscription, productPlanId) {
            yield subscription.applyChangePlan(productPlanId);
        }), this.changePaymentDetails = flow(function*(subscription) {
            yield subscription.loadVendorScripts();
        }), this.cancelSubscription = flow(function*(subscription, input) {
            yield subscription.cancel(input);
        }), this.createAffiliate = flow(function*(affiliate, user, inviteUuid) {
            const { session } = this;
            if (session) {
                yield session.probablyRenewJwt();
            }
            try {
                const result = yield realCommerceRequest({
                    location: routeLocationAffiliatePost,
                    request: {
                        affiliate,
                        affiliateInvite: inviteUuid ? {
                            uuid: inviteUuid
                        } : undefined,
                        user: session ? undefined : user
                    },
                    multipart: true,
                    settings: session ? {
                        headers: session.authorizationHeader
                    } : {}
                });
                if (session) {
                    try {
                        yield session.fetchAffiliate();
                    } catch (e) {
                    // Ignored on purpose — the affiliate status is surfaced via session.affiliateError
                    // (e.g. AffiliateStatusIsPending routes the user to the pending panel).
                    }
                }
                return result;
            } catch (e) {
                throw new RealCommerceError(e);
            }
        }), this.fetchAffiliateProducts = flow(function*() {
            this.isBusyAffiliateProducts = true;
            try {
                const { products } = yield realCommerceRequest({
                    location: routeLocationAffiliateProductsGet
                });
                this.affiliateProducts = products;
            } catch (e) {
                throw new RealCommerceError(e);
            } finally{
                this.isBusyAffiliateProducts = false;
            }
        }), this.createAccountEnvato = flow(function*(data) {
            const { session } = this;
            if (session) {
                yield session.probablyRenewJwt();
            }
            try {
                return yield realCommerceRequest({
                    location: routeLocationEnvatoPurchasePost,
                    request: {
                        envatoPurchase: {
                            purchaseCode: data.purchaseCode,
                            ...session ? {} : {
                                firstName: data.firstName,
                                lastName: data.lastName,
                                email: data.email
                            },
                            isTermsAccepted: true
                        }
                    },
                    settings: session ? {
                        headers: session.authorizationHeader
                    } : {}
                });
            } catch (e) {
                throw new RealCommerceError(e);
            }
        }), this.fetchPaymentFailedDetails = flow(function*(subscriptionUuid) {
            try {
                const { subscription, affectedHostnames } = yield realCommerceRequest({
                    location: routeLocationPaymentFailedPaymentDetailsGet,
                    params: {
                        subscriptionUuid
                    }
                });
                this.expiredPaymentDetailsSubscription = new PaddleSubscription(subscription, undefined);
                this.expiredPaymentAffectedHostnames = affectedHostnames;
            } catch (e) {
                throw new RealCommerceError(e);
            }
        });
        this.rootStore = rootStore;
        // Synchronous hydration probe — we have credentials to validate
        // when either a stored JWT exists in localStorage or the URL carries
        // a `magic-link-token` (deep-link from a magic-link email).
        const params = new URLSearchParams(window.location.search);
        const hasMagicLinkToken = !!params.get("magic-link-token");
        const hasConfirmationCode = !!params.get("user-confirmation-code");
        const hasStoredJwt = localStorageTest() && !!localStorage.getItem(LOCAL_STORAGE_JWT_TOKEN_NAME);
        this.isHydrating = hasMagicLinkToken || hasConfirmationCode || hasStoredJwt;
    }
    setFatalError(error) {
        this.fatalError = error;
    }
    signOut() {
        this.session?.logout().catch(()=>undefined);
        this.session = undefined;
        if (localStorageTest()) {
            localStorage.removeItem(LOCAL_STORAGE_JWT_TOKEN_NAME);
        }
    }
    flashLicense(licenseId, durationMs = 1800) {
        if (this.flashTimeout) {
            clearTimeout(this.flashTimeout);
        }
        this.flashLicenseId = licenseId;
        this.flashTimeout = setTimeout(()=>{
            runInAction(()=>{
                this.flashLicenseId = undefined;
                this.flashTimeout = undefined;
            });
        }, durationMs);
    }
    flashSubscription(subscriptionId, durationMs = 1800) {
        if (this.flashSubscriptionTimeout) {
            clearTimeout(this.flashSubscriptionTimeout);
        }
        this.flashSubscriptionId = subscriptionId;
        this.flashSubscriptionTimeout = setTimeout(()=>{
            runInAction(()=>{
                this.flashSubscriptionId = undefined;
                this.flashSubscriptionTimeout = undefined;
            });
        }, durationMs);
    }
    finishHydration() {
        this.isHydrating = false;
    }
    resetSubmitted() {
        this.submitted = undefined;
        this.rootStore.notificationStore.clearChannel(NotificationChannel.accountV2Unauth);
    }
    activeSubscriptionActionDialogFor(subscriptionId, kind) {
        this.activeSubscriptionActionDialog = {
            subscriptionId,
            kind
        };
    }
    openChangePlanFor(subscription, options) {
        this.activeSubscriptionActionDialog = {
            subscriptionId: subscription.data.id,
            kind: "change-plan",
            autoSelectFirstUpgrade: options?.autoSelectFirstUpgrade ?? false
        };
        trackAnalyticsEvent("change_plan_opened", {
            ga: {
                event_category: "ecommerce",
                event_label: "Change plan opened"
            },
            matomo: {
                category: "ecommerce",
                name: "Change plan opened"
            }
        });
    }
    openChangePaymentFor(subscription) {
        this.activeSubscriptionActionDialog = {
            subscriptionId: subscription.data.id,
            kind: "change-payment"
        };
        trackAnalyticsEvent("change_payment_details_opened", {
            ga: {
                event_category: "account",
                event_label: "Change payment details opened"
            },
            matomo: {
                category: "account",
                name: "Change payment details opened"
            }
        });
    }
    openCancellationFor(subscription) {
        this.activeSubscriptionActionDialog = {
            subscriptionId: subscription.data.id,
            kind: "cancellation"
        };
    }
    openChangeInvoiceAddressFor(subscription) {
        this.activeSubscriptionActionDialog = {
            subscriptionId: subscription.data.id,
            kind: "change-invoice-address"
        };
    }
    closeSubscriptionModal() {
        this.activeSubscriptionActionDialog = undefined;
    }
}
_ts_decorate([
    observable,
    _ts_metadata("design:type", typeof UserSession === "undefined" ? Object : UserSession)
], AccountV2Store.prototype, "session", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Boolean)
], AccountV2Store.prototype, "isHydrating", void 0);
_ts_decorate([
    observable
], AccountV2Store.prototype, "isBusyAffiliateProducts", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], AccountV2Store.prototype, "affiliateProducts", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", typeof SubscriptionActionDialog === "undefined" ? Object : SubscriptionActionDialog)
], AccountV2Store.prototype, "activeSubscriptionActionDialog", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", typeof Subscription === "undefined" ? Object : Subscription)
], AccountV2Store.prototype, "expiredPaymentDetailsSubscription", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Array)
], AccountV2Store.prototype, "expiredPaymentAffectedHostnames", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", typeof RealCommerceError === "undefined" ? Object : RealCommerceError)
], AccountV2Store.prototype, "fatalError", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", String)
], AccountV2Store.prototype, "flashLicenseId", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", String)
], AccountV2Store.prototype, "flashSubscriptionId", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], AccountV2Store.prototype, "submitted", void 0);
_ts_decorate([
    computed,
    _ts_metadata("design:type", Boolean),
    _ts_metadata("design:paramtypes", [])
], AccountV2Store.prototype, "isLoggedIn", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", Boolean),
    _ts_metadata("design:paramtypes", [])
], AccountV2Store.prototype, "isAffiliate", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        Object
    ]),
    _ts_metadata("design:returntype", void 0)
], AccountV2Store.prototype, "setFatalError", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", []),
    _ts_metadata("design:returntype", void 0)
], AccountV2Store.prototype, "signOut", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        String,
        void 0
    ]),
    _ts_metadata("design:returntype", void 0)
], AccountV2Store.prototype, "flashLicense", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        String,
        void 0
    ]),
    _ts_metadata("design:returntype", void 0)
], AccountV2Store.prototype, "flashSubscription", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", []),
    _ts_metadata("design:returntype", void 0)
], AccountV2Store.prototype, "finishHydration", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", []),
    _ts_metadata("design:returntype", void 0)
], AccountV2Store.prototype, "resetSubmitted", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        String,
        typeof SubscriptionModalKind === "undefined" ? Object : SubscriptionModalKind
    ]),
    _ts_metadata("design:returntype", void 0)
], AccountV2Store.prototype, "activeSubscriptionActionDialogFor", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        typeof Subscription === "undefined" ? Object : Subscription,
        Object
    ]),
    _ts_metadata("design:returntype", void 0)
], AccountV2Store.prototype, "openChangePlanFor", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        typeof Subscription === "undefined" ? Object : Subscription
    ]),
    _ts_metadata("design:returntype", void 0)
], AccountV2Store.prototype, "openChangePaymentFor", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        typeof Subscription === "undefined" ? Object : Subscription
    ]),
    _ts_metadata("design:returntype", void 0)
], AccountV2Store.prototype, "openCancellationFor", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        typeof Subscription === "undefined" ? Object : Subscription
    ]),
    _ts_metadata("design:returntype", void 0)
], AccountV2Store.prototype, "openChangeInvoiceAddressFor", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", []),
    _ts_metadata("design:returntype", void 0)
], AccountV2Store.prototype, "closeSubscriptionModal", null);
export { AccountV2Store };
